import { CameraIcon, X } from "lucide-react";
import { Button } from "primereact/button";
import { Image } from "primereact/image";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import heic2any from "heic2any";
import { formatTimestamp } from "./helper/helperFunctions";
import { showError, showSuccess } from "../../../util/ShowStatus";

interface PhotoUploadProps {
  label?: string;
  images: Record<string, File>;
  isUploadInProgress: boolean;
  setIsUploadInProgress: any;
  setImages: (files: Record<string, File>) => void;
  formValues: any;
  setFormValues: any;
  fileValues: any[];
  timeStampLocationValues: {
    locations: Array<{
      userID: string;
      address: string;
      coordinates?: { latitude: number; longitude: number };
    }>;
    timeStamps: Array<{ userID: string; timestamp: string }>;
  };
  setTimeStampLocationValues: (values: any) => void;
  setFileValues: (files: any) => void;
  classname?: string;
  maxImages?: number;
  type: string;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  label = "Upload Photo",
  images,
  isUploadInProgress,
  setIsUploadInProgress,
  setImages,
  formValues,
  setFormValues,
  setTimeStampLocationValues,
  setFileValues,
  classname,
  maxImages = 1,
  type,
}) => {
  const toast = useRef<Toast>(null);
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const prevAdditionalMembersRef = useRef<any[]>([]);
  const [isGettingLocation, setIsGettingLocation] = useState<boolean>(false);
  const [uploadingUserId, setUploadingUserId] = useState<string | null>(null);

  const ALLOWED_TYPES = [
    "image/jpeg",
    "image/png",
    "image/heic",
    "image/heif",
    "image/webp",
  ];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 5MB

  useEffect(() => {
    if (type === "additionalTeamMember") {
      const currentMembers = formValues.additionalTeamMembers || [];
      const prevMembers = prevAdditionalMembersRef.current;

      const removedMembers = prevMembers.filter(
        (prevMember) =>
          !currentMembers.some(
            (current: any) => current.userID === prevMember.userID
          )
      );

      if (removedMembers.length > 0) {
        const updatedImages = { ...images };
        removedMembers.forEach((member) => {
          if (updatedImages[member.userID]) {
            delete updatedImages[member.userID];
          }
        });

        if (Object.keys(updatedImages).length !== Object.keys(images).length) {
          setImages(updatedImages);
          setFileValues((prev: any[]) => {
            return prev.filter(
              (item) =>
                !removedMembers.some((member) => member.userID === item.userID)
            );
          });

          setTimeStampLocationValues((prev: any) => {
            return {
              locations: prev.locations.filter(
                (loc: any) =>
                  !removedMembers.some((member) => member.userID === loc.userID)
              ),
              timeStamps: prev.timeStamps.filter(
                (ts: any) =>
                  !removedMembers.some((member) => member.userID === ts.userID)
              ),
            };
          });
        }
      }

      prevAdditionalMembersRef.current = [...currentMembers];
    }
  }, [
    formValues.additionalTeamMembers,
    images,
    setImages,
    setFileValues,
    setTimeStampLocationValues,
    type,
  ]);

  const convertHeicToJpeg = async (file: File): Promise<File> => {
    const fileType =
      file.type || `image/${file.name.split(".").pop()?.toLowerCase()}`;
    if (fileType === "image/heic" || fileType === "image/heif") {
      try {
        const conversionResult = (await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.8,
        })) as Blob;

        return new File(
          [conversionResult],
          file.name.replace(/\.[^/.]+$/, ".jpg"),
          {
            type: "image/jpeg",
            lastModified: new Date().getTime(),
          }
        );
      } catch (error) {
        console.error("HEIC conversion failed:", error);
        throw new Error("Failed to convert HEIC image");
      }
    }
    return file;
  };

  const validateFile = (file: File): boolean => {
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    if (!ALLOWED_TYPES.includes(file.type.toLowerCase()) && file.type !== "") {
      showError(
        `Unsupported file type: ${file.type}. Please upload JPEG, PNG, or HEIC.`,
        toast
      );
      return false;
    } else if (
      fileExtension &&
      !ALLOWED_TYPES.includes(`image/${fileExtension}`) &&
      file.type === ""
    ) {
      showError(
        `Unsupported file type: ${fileExtension}. Please upload JPEG, PNG, or HEIC.`,
        toast
      );
      return false;
    } else if (!fileExtension && file.type === "") {
      showError(
        `Unsupported file type: ${fileExtension}. Please upload JPEG, PNG, or HEIC.`,
        toast
      );
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      showError(
        `File is too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.`,
        toast
      );
      return false;
    }

    return true;
  };

  const getAddressFromCoordinates = async (
    latitude: number,
    longitude: number
  ): Promise<string> => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
        {
          headers: {
            "Accept-Language": "en",
            "User-Agent": "PhotoUploadApp/1.0",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch address");
      }

      const data = await response.json();
      const address = formatAddress(data.address);
      return address;
    } catch (error) {
      console.error("Error getting address:", error);
      return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
    }
  };

  const formatAddress = (addressData: any): string => {
    const components = [];

    if (addressData.suburb) components.push(addressData.suburb);
    else if (addressData.neighbourhood)
      components.push(addressData.neighbourhood);
    else if (addressData.quarter) components.push(addressData.quarter);

    if (addressData.state_district) components.push(addressData.state_district);
    else if (addressData.town) components.push(addressData.town);
    else if (addressData.village) components.push(addressData.village);

    if (addressData.state) components.push(addressData.state);
    if (addressData.country) components.push(addressData.country);
    if (addressData.postcode) components.push(addressData.postcode);

    return components.join(", ");
  };

  const getCurrentLocation = async (): Promise<{
    address: string;
    coordinates: { latitude: number; longitude: number };
  } | null> => {
    if (!navigator.geolocation) {
      showError("Geolocation is not supported by your browser", toast);
      return null;
    }

    setIsGettingLocation(true);

    try {
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          });
        }
      );

      const coordinates = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };

      const address = await getAddressFromCoordinates(
        coordinates.latitude,
        coordinates.longitude
      );

      return {
        address,
        coordinates,
      };
    } catch (error: any) {
      console.error("Geolocation error:", error);
      return null;
    } finally {
      setIsGettingLocation(false);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    memberId?: string
  ) => {
    if (!event.target.files || !event.target.files[0]) return;

    const originalFile = event.target.files[0];
    if (!validateFile(originalFile)) {
      event.target.value = "";
      return;
    }

    const userId = memberId || "default";
    setUploadingUserId(userId);
    setIsUploadInProgress(true);

    try {
      const locationData = await getCurrentLocation();
      const timestamp = formatTimestamp(new Date());

      let userId = memberId || "default";
      if (type === "teamMember1" && formValues.teamMember1?.userID) {
        userId = formValues.teamMember1.userID;
      } else if (type === "teamMember2" && formValues.teamMember2?.userID) {
        userId = formValues.teamMember2.userID;
      }

      setTimeStampLocationValues((prev: any) => {
        const filteredLocations = prev.locations.filter(
          (loc: any) => loc.userID !== userId
        );
        const filteredTimeStamps = prev.timeStamps.filter(
          (ts: any) => ts.userID !== userId
        );

        if (!locationData) {
          return {
            locations: filteredLocations,
            timeStamps: filteredTimeStamps,
          };
        }
        return {
          locations: [
            ...filteredLocations,
            {
              userID: userId,
              address: locationData.address,
              coordinates: locationData.coordinates,
            },
          ],
          timeStamps: [...filteredTimeStamps, { userID: userId, timestamp }],
        };
      });

      const file = await convertHeicToJpeg(originalFile);

      if (type === "additionalTeamMember" && memberId) {
        const newImages = { ...images };
        newImages[memberId] = file;
        setImages(newImages);

        setFormValues((prev: any) => ({
          ...prev,
          additionalTeamMembers: prev.additionalTeamMembers.map((member: any) =>
            member.userID === memberId ? { ...member, photo: file } : member
          ),
        }));

        setFileValues((prev: any[]) => {
          const existingIndex = prev.findIndex(
            (item) => item.userID === memberId
          );
          if (existingIndex !== -1) {
            const updated = [...prev];
            updated[existingIndex] = {
              userID: memberId,
              images: { default: file },
            };
            return updated;
          } else {
            return [
              ...prev,
              {
                userID: memberId,
                images: { default: file },
              },
            ];
          }
        });
      } else if (type === "teamMember1" && formValues.teamMember1?.userID) {
        setImages({ ...images, default: file });
        setFileValues((prev: any[]) => {
          const userId = formValues.teamMember1.userID;
          const existingIndex = prev.findIndex(
            (item) => item.userID === userId
          );
          if (existingIndex !== -1) {
            const updated = [...prev];
            updated[existingIndex] = {
              userID: userId,
              images: { default: file },
            };
            return updated;
          } else {
            return [
              ...prev,
              {
                userID: userId,
                images: { default: file },
              },
            ];
          }
        });
      } else if (type === "teamMember2" && formValues.teamMember2?.userID) {
        setImages({ ...images, default: file });
        setFileValues((prev: any[]) => {
          const userId = formValues.teamMember2.userID;
          const existingIndex = prev.findIndex(
            (item) => item.userID === userId
          );
          if (existingIndex !== -1) {
            const updated = [...prev];
            updated[existingIndex] = {
              userID: userId,
              images: { default: file },
            };
            return updated;
          } else {
            return [
              ...prev,
              {
                userID: userId,
                images: { default: file },
              },
            ];
          }
        });
      }

      showSuccess("Photo uploaded successfully!", toast);
    } catch (error) {
      showError("Failed to process image. Please try again.", toast);
      console.error("Image processing error:", error);
    } finally {
      setIsUploadInProgress(false);
      setUploadingUserId(null);
      event.target.value = "";
    }
  };

  const handleRemoveImage = (memberId: string) => {
    const newImages = { ...images };
    delete newImages[memberId];
    setImages(newImages);

    let userId = memberId;
    if (type === "teamMember1" && formValues.teamMember1?.userID) {
      userId = formValues.teamMember1.userID;
    } else if (type === "teamMember2" && formValues.teamMember2?.userID) {
      userId = formValues.teamMember2.userID;
    }

    setTimeStampLocationValues((prev: any) => ({
      locations: prev.locations.filter((loc: any) => loc.userID !== userId),
      timeStamps: prev.timeStamps.filter((ts: any) => ts.userID !== userId),
    }));

    if (type === "additionalTeamMember") {
      setFormValues({
        ...formValues,
        additionalTeamMembers: formValues.additionalTeamMembers.map(
          (member: any) =>
            member.userID === memberId ? { ...member, photo: null } : member
        ),
      });

      setFileValues((prev: any[]) =>
        prev.filter((item) => item.userID !== memberId)
      );
    } else if (type === "teamMember1" && formValues.teamMember1?.userID) {
      setFileValues((prev: any[]) =>
        prev.filter((item) => item.userID !== formValues.teamMember1.userID)
      );
    } else if (type === "teamMember2" && formValues.teamMember2?.userID) {
      setFileValues((prev: any[]) =>
        prev.filter((item) => item.userID !== formValues.teamMember2.userID)
      );
    }
  };

  const triggerFileInput = (memberId?: string) => {
    if (isUploadInProgress) {
      showError("Please wait until the current upload completes", toast);
      return;
    }

    if (maxImages === 0) {
      showError("Please select a user to capture the photo", toast);
      return;
    }

    const inputId = memberId || "default";
    if (fileInputRefs.current[inputId]) {
      fileInputRefs.current[inputId]?.click();
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <div className={`tw-flex tw-flex-col tw-gap-1 tw-w-full tw-flex-1`}>
        <label className="leak-label">{label}</label>

        <div
          className={`tw-flex tw-justify-center tw-items-center tw-min-h-[100px] tw-border-solid tw-border-[1px] tw-border-[#D0D5DD] tw-rounded-lg tw-relative ${classname}`}
        >
          {type === "additionalTeamMember" &&
          formValues.additionalTeamMembers?.length > 0 ? (
            <div className="tw-flex tw-flex-wrap tw-gap-4 tw-p-4 tw-w-full">
              {formValues.additionalTeamMembers.map((member: any) => (
                <div
                  key={member.userID}
                  className="tw-flex tw-flex-col tw-items-start lg:tw-items-center tw-gap-2 "
                >
                  <span className="tw-text-sm">{member.displayName}</span>
                  <div
                    className="tw-flex tw-justify-center tw-items-center tw-h-20 tw-w-20 tw-border-solid tw-border-[1px] tw-border-[#D0D5DD] tw-rounded-lg tw-relative tw-cursor-pointer"
                    onClick={() => triggerFileInput(member.userID)}
                  >
                    {images[member.userID] ? (
                      <div className="tw-relative tw-w-20 tw-h-20 tw-bg-gray-200 tw-rounded-lg">
                        <Image
                          src={URL.createObjectURL(images[member.userID])}
                          alt={`Uploaded ${member.displayName}`}
                          className="tw-w-20 tw-h-20 tw-inline-flex tw-object-contain"
                        />
                        {/* // Add this to your image preview */}
                        <div className="tw-absolute tw-bottom-1 tw-left-1 tw-bg-black/50 tw-text-white tw-text-[8px] tw-px-1 tw-py-0.5 tw-rounded">
                          {images[member.userID].name
                            .split(".")
                            .pop()
                            ?.toUpperCase()}{" "}
                          • {(images[member.userID].size / 1024).toFixed(1)} KB
                        </div>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage(member.userID);
                          }}
                          className="tw-absolute tw-top-1 tw-right-1 tw-bg-gray-500 tw-text-white tw-rounded-full tw-p-1 tw-text-xs tw-flex tw-items-center tw-justify-center"
                        >
                          <X size={12} />
                        </Button>
                      </div>
                    ) : uploadingUserId === member.userID ? (
                      <div className="tw-text-xs tw-text-gray-500 tw-text-center">
                        {isUploadInProgress
                          ? "Getting location..."
                          : "Getting location..."}
                      </div>
                    ) : (
                      <CameraIcon className="tw-text-xl tw-text-gray-500" />
                    )}
                  </div>
                  <input
                    id={`${label}-${member.userID}`}
                    ref={(el) => (fileInputRefs.current[member.userID] = el)}
                    type="file"
                    className="tw-hidden"
                    onChange={(e) => handleFileChange(e, member.userID)}
                    accept="image/*"
                    capture="environment"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div
              className="tw-w-full tw-h-full tw-flex tw-justify-center tw-items-center tw-cursor-pointer"
              onClick={() => triggerFileInput()}
            >
              {!images["default"] ? (
                isGettingLocation || uploadingUserId === "default" ? (
                  <div className="tw-text-sm tw-text-gray-500">
                    {isUploadInProgress
                      ? "Getting location..."
                      : "Getting location..."}
                  </div>
                ) : (
                  <CameraIcon className="tw-text-xl tw-text-gray-500" />
                )
              ) : (
                <div className="tw-flex tw-flex-wrap tw-gap-2 tw-p-2 tw-w-full">
                  <div className="tw-relative tw-w-20 tw-h-20 tw-bg-gray-200 tw-rounded-lg">
                    <Image
                      src={URL.createObjectURL(images["default"])}
                      alt="Uploaded photo"
                      className="tw-w-20 tw-h-20 tw-inline-flex tw-object-contain"
                    />
                    {/* // Add this to your image preview */}
                    <div className="tw-absolute tw-bottom-1 tw-left-1 tw-bg-black/50 tw-text-white tw-text-[8px] tw-px-1 tw-py-0.5 tw-rounded">
                      {images["default"].name.split(".").pop()?.toUpperCase()} •{" "}
                      {(images["default"].size / 1024).toFixed(1)} KB
                    </div>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveImage("default");
                      }}
                      className="tw-absolute tw-top-1 tw-right-1 tw-bg-gray-500 tw-text-white tw-rounded-full tw-p-1 tw-text-xs tw-flex tw-items-center tw-justify-center"
                      disabled={isUploadInProgress}
                    >
                      <X size={12} />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {type !== "additionalTeamMember" && (
          <input
            id={label}
            ref={(el) => (fileInputRefs.current["default"] = el)}
            type="file"
            className="tw-hidden"
            onChange={handleFileChange}
            accept="image/*"
            capture="environment"
          />
        )}
      </div>
    </>
  );
};

export default PhotoUpload;
